import api from "../api";
import {
  Client,
  ClientCreatePayload,
  ClientsResponse,
  ClientUpdatePayload,
  PlatformUser,
  PlatformUsersResponse,
  PlatformUserCreateResponse,
  AdminFolderAccess,
  FolderSubfolderOption,
  PlatformIntegrationsResponse,
  AdminChatBotProfile,
  BackendUser,
  BackendUsersResponse,
  AdminUserIntegrations,
  AdminVendorConnection,
  UserType,
} from "types/interfaces/adminUsers";

export interface GetAdminUsersParams {
  page?: number;
  search?: string;
  page_size?: number;
  id?: number;
  role?: string;
  user_type?: UserType;
  client_id?: number;
  sort_by?:
    | "name"
    | "username"
    | "email"
    | "date_joined"
    | "last_login"
    | "folders"
    | "integrations"
    | "sftp"
    | "client_name"
    | "status"
    | "roles"
    | "assigned_clients"
    | "s3_user"
    | "bot_jobs";
  sort_order?: "asc" | "desc";
}

export interface GetClientsParams {
  page?: number;
  search?: string;
  page_size?: number;
  is_active?: boolean;
  sort_by?:
    | "company_name"
    | "company_code"
    | "created_at"
    | "owner"
    | "status"
    | "users";
  sort_order?: "asc" | "desc";
  owner_id?: number;
}

export type BackendUserLabel = "migration" | "integration" | "sftp" | "bots";

export interface GetBackendUsersParams extends GetAdminUsersParams {
  label?: BackendUserLabel;
}

export interface UpdateAdminUserPayload {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  is_partnership_team?: boolean;
  is_calibrate_manager?: boolean;
  can_download_top_document_folders?: boolean;
  is_secure_file_upload_user?: boolean;
  is_pass_reset?: boolean;
  owned_client_ids?: number[];
}

export interface CreateAdminUserPayload {
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  user_type?: "internal" | "client";
  client_id?: number | null;
  is_data_storage_user?: boolean;
  is_integration_dashboard_user?: boolean;
  is_ai_agent_user?: boolean;
  is_partnership_team?: boolean;
  can_download_top_document_folders?: boolean;
  is_secure_file_upload_user?: boolean;
  owned_client_ids?: number[];
}

export interface AvailabilityResponse {
  available: boolean;
  message?: string;
}

export interface BotRegistryItem {
  id: number;
  bot_id: string;
  module_path: string;
  vendor: string;
  job_kind: string;
  bot_slug: string;
  supported_job_types?: string[];
  aws_ready: boolean;
  is_active: boolean;
}

/** Parent grouping for bot automation under a Django user (see `/tasks/major-jobs/`). */
export interface MajorJob {
  id: number;
  user: number;
  name: string;
  vendor: string;
  bot_kind: string;
  included_connection_ids: number[];
  child_job_ids: number[];
  ssn_file_exists: boolean | null;
}

const PLATFORM_BASE = "/api/admin/platform-users";
const BACKEND_BASE = "/api/admin/users";
const CLIENTS_BASE = "/api/admin/clients";

const adminUsersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<PlatformUsersResponse, GetAdminUsersParams>({
      query: ({
        page = 1,
        search,
        page_size,
        role,
        user_type,
        client_id,
        sort_by,
        sort_order,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (role) params.set("role", role);
        if (user_type) params.set("user_type", user_type);
        if (client_id) params.set("client_id", String(client_id));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        return `${PLATFORM_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getClients: builder.query<ClientsResponse, GetClientsParams>({
      query: ({
        page = 1,
        search,
        page_size,
        is_active,
        sort_by,
        sort_order,
        owner_id,
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (is_active !== undefined) params.set("is_active", String(is_active));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        if (owner_id) params.set("owner_id", String(owner_id));
        return `${CLIENTS_BASE}/?${params.toString()}`;
      },
      providesTags: ["AdminClients"],
    }),

    getClient: builder.query<Client, number>({
      query: (id) => `${CLIENTS_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminClients", id }],
    }),

    createClient: builder.mutation<Client, ClientCreatePayload>({
      query: (body) => ({
        url: `${CLIENTS_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminClients"],
    }),

    updateClient: builder.mutation<
      Client,
      { id: number } & ClientUpdatePayload
    >({
      query: ({ id, ...body }) => ({
        url: `${CLIENTS_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdminClients", id },
        "AdminClients",
        "AdminUsers",
      ],
    }),

    archiveClient: builder.mutation<
      Client,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${CLIENTS_BASE}/${id}/archive/`,
        method: "PATCH",
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminClients", "AdminUsers"],
    }),

    checkClientCodeAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-code/?${params.toString()}`;
      },
    }),

    checkClientNameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${CLIENTS_BASE}/check-name/?${params.toString()}`;
      },
    }),

    getAdminUser: builder.query<PlatformUser, number>({
      query: (id) => `${PLATFORM_BASE}/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id }],
    }),

    checkPlatformUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkPlatformEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${PLATFORM_BASE}/check-email/?${params.toString()}`;
      },
    }),

    updateAdminUser: builder.mutation<PlatformUser, UpdateAdminUserPayload>({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        "AdminUsers",
        "Auth",
        { type: "FileSystem" as const, id: "__root__" },
      ],
    }),

    createAdminUser: builder.mutation<PlatformUserCreateResponse, CreateAdminUserPayload>({
      query: (body) => ({
        url: `${PLATFORM_BASE}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    archiveAdminUser: builder.mutation<
      PlatformUser,
      { id: number; archiveDate?: string | null }
    >({
      query: ({ id, archiveDate }) => ({
        url: `${PLATFORM_BASE}/${id}/archive/`,
        method: "PATCH",
        body: archiveDate !== undefined ? { archive_date: archiveDate } : {},
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    resetAdminUserPassword: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/reset-password/`,
        method: "POST",
      }),
    }),

    generateAdminUserPassword: builder.mutation<{ generated_password: string }, number>({
      query: (id) => ({
        url: `${PLATFORM_BASE}/${id}/generate-password/`,
        method: "POST",
      }),
    }),

    getAdminUserFolders: builder.query<
      AdminFolderAccess[],
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/folders/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `folders-${id}` },
      ],
    }),

    getAssignableFolders: builder.query<
      AdminFolderAccess[],
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-folders/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    getAssignableIntegrations: builder.query<
      PlatformIntegrationsResponse,
      number | undefined
    >({
      query: (client_id) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/assignable-integrations/${params}`;
      },
      providesTags: ["AdminUsers"],
    }),

    grantFolderAccess: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders?: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/grant/`,
        method: "POST",
        body: { allowed_subfolders: allowedSubfolders ?? [] },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    updateFolderSubfolders: builder.mutation<
      void,
      { userId: number; folderId: number; allowedSubfolders: string[] }
    >({
      query: ({ userId, folderId, allowedSubfolders }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/subfolders/`,
        method: "PATCH",
        body: { allowed_subfolders: allowedSubfolders },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
      ],
    }),

    getFolderSubfolders: builder.query<
      { subfolders: FolderSubfolderOption[] },
      number
    >({
      query: (folderId) => `${PLATFORM_BASE}/folders/${folderId}/subfolders/`,
    }),

    revokeFolderAccess: builder.mutation<void, { userId: number; folderId: number }>({
      query: ({ userId, folderId }) => ({
        url: `${PLATFORM_BASE}/${userId}/folders/${folderId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminUserIntegrations: builder.query<
      PlatformIntegrationsResponse,
      { id: number; client_id?: number }
    >({
      query: ({ id, client_id }) => {
        const params = client_id ? `?client_id=${client_id}` : "";
        return `${PLATFORM_BASE}/${id}/integrations/${params}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "AdminUsers", id: `integrations-${id}` },
      ],
    }),

    grantIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/grant/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    revokeIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `${PLATFORM_BASE}/${userId}/integrations/${matchId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminChatBotProfile: builder.query<AdminChatBotProfile, number>({
      query: (userId) => `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
      providesTags: (_result, _error, userId) => [{ type: "AdminUsers", id: `chat-bot-profile-${userId}` }],
    }),

    createAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      { userId: number; chat_id?: string; api_key?: string; name?: string; email?: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
        "AdminUsers",
      ],
    }),

    updateAdminChatBotProfile: builder.mutation<
      AdminChatBotProfile,
      { userId: number; chat_id?: string; api_key?: string }
    >({
      query: ({ userId, ...body }) => ({
        url: `${PLATFORM_BASE}/${userId}/chat-bot-profile/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
      ],
    }),

    updateAdminUserPermissions: builder.mutation<
      PlatformUser,
      {
        id: number;
        is_calibrate_manager: boolean;
        can_download_top_document_folders: boolean;
        is_partnership_team?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `${PLATFORM_BASE}/${id}/permissions/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    getBackendUsers: builder.query<BackendUsersResponse, GetBackendUsersParams>({
      query: ({ page = 1, search, page_size, id, sort_by, sort_order, label } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (id != null) params.set("id", String(id));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        if (label) params.set("label", label);
        return `${BACKEND_BASE}/?${params.toString()}`;
      },
      transformResponse: (response: {
        count: number;
        next: string | null;
        previous: string | null;
        results: Array<{
          id: number;
          username: string;
          first_name: string;
          last_name: string;
          client_name?: string | null;
          client_id?: number | null;
          folder_count: number;
          integration_count: number;
          platform_integration_count?: number;
          sftp_connection_count?: number;
          major_job_count?: number;
          date_joined: string;
          last_login: string | null;
        }>;
      }): BackendUsersResponse => ({
        count: response.count,
        next: response.next,
        previous: response.previous,
        results: response.results.map((u) => ({
          id: u.id,
          username: u.username,
          first_name: u.first_name ?? "",
          last_name: u.last_name ?? "",
          client_name: u.client_name ?? null,
          client_id: u.client_id ?? null,
          folder_count: u.folder_count,
          integration_count: u.integration_count,
          platform_integration_count: u.platform_integration_count ?? 0,
          sftp_count: u.sftp_connection_count ?? 0,
          major_job_count: u.major_job_count ?? 0,
          date_joined: u.date_joined,
          last_login: u.last_login,
        })),
      }),
      providesTags: ["AdminUsers"],
    }),

    checkBackendUsernameAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-username/?${params.toString()}`;
      },
    }),

    checkBackendEmailAvailability: builder.query<
      AvailabilityResponse,
      { value: string; excludeId?: number }
    >({
      query: ({ value, excludeId }) => {
        const params = new URLSearchParams();
        params.set("value", value);
        if (excludeId != null) params.set("exclude_id", String(excludeId));
        return `${BACKEND_BASE}/check-email/?${params.toString()}`;
      },
    }),

    getBackendUserFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `${BACKEND_BASE}/${id}/folders/owned/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `backend-folders-${id}` }],
    }),

    getBackendUserStorageFolders: builder.query<
      { folders: Array<{ id: number; display_name: string; folder_prefix: string; subfolders: string[] }> },
      number
    >({
      query: (id) => `${BACKEND_BASE}/${id}/storage-folders/`,
    }),

    getBackendUserIntegrations: builder.query<AdminUserIntegrations, number>({
      query: (id) => `${BACKEND_BASE}/${id}/integrations/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `backend-integrations-${id}` }],
    }),

    getBackendUserBotConnections: builder.query<
      { connections: AdminVendorConnection[] },
      number
    >({
      query: (id) => `${BACKEND_BASE}/${id}/bot-connections/`,
      providesTags: (_result, _error, id) => [
        { type: "AdminUsers", id: `backend-bot-connections-${id}` },
      ],
    }),

    createBackendUserBotConnection: builder.mutation<
      { status: string; message: { data: AdminVendorConnection } },
      {
        userId: number;
        body: Partial<AdminVendorConnection> & { type?: string };
      }
    >({
      query: ({ userId, body }) => ({
        url: "/connections/",
        method: "POST",
        body: {
          user_id: userId,
          type: body.type ?? "bot",
          host: body.host ?? "",
          client_code: body.client_code ?? "",
          username: body.username ?? "",
          password: body.password ?? "",
          port: body.port ?? "9223",
          vendor: body.vendor ?? "",
        },
      }),
    }),

    getConnectionTasks: builder.query<
      {
        tasks: Array<{
          id: number;
          job?: number | null;
          task_status: string;
          bot_work_status: string;
          task2fa_status?: string;
          bot_name: string;
          document_type: string;
          employee_number_from: number;
          employee_number_to: number;
          browser_profile_id?: number;
        }>;
      },
      number
    >({
      query: (connectionId) => `/api/admin/connections/${connectionId}/tasks/`,
      providesTags: (_result, _error, connectionId) => [
        { type: "AdminUsers", id: `connection-tasks-${connectionId}` },
      ],
    }),

    createConnectionTask: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({
        url: "/tasks/",
        method: "POST",
        body,
      }),
    }),

    addTasksToJob: builder.mutation<
      { tasks: Record<string, unknown>[] },
      { jobId: number; count: number; connection_id?: number }
    >({
      query: ({ jobId, count, connection_id }) => ({
        url: `/tasks/jobs/${jobId}/`,
        method: "POST",
        body:
          connection_id != null ? { count, connection_id } : { count },
      }),
    }),

    createTasksBatch: builder.mutation<
      { tasks: Record<string, unknown>[]; job?: Record<string, unknown> },
      Record<string, unknown>
    >({
      query: (body) => ({
        url: "/tasks/batch/",
        method: "POST",
        body,
      }),
    }),

    getBotRegistry: builder.query<
      { count?: number; results?: BotRegistryItem[] } | BotRegistryItem[],
      { vendor?: string; jobKind?: string; isActive?: boolean; awsReady?: boolean; pageSize?: number }
    >({
      query: ({ vendor, jobKind, isActive = true, awsReady, pageSize = 200 }) => {
        const params = new URLSearchParams();
        if (vendor) params.append("vendor", vendor);
        if (jobKind) params.append("job_kind", jobKind);
        if (isActive != null) params.append("is_active", String(isActive));
        if (awsReady != null) params.append("aws_ready", String(awsReady));
        params.append("page_size", String(pageSize));
        return `/api/admin/bots/?${params.toString()}`;
      },
    }),

    getTaskJobs: builder.query<Record<string, unknown>[], { connectionId?: number; userId?: number; botName?: string; majorJobId?: number }>({
      query: ({ connectionId, userId, botName, majorJobId }) => {
        const params = new URLSearchParams();
        if (connectionId != null) params.append("connection_id", String(connectionId));
        if (userId != null) params.append("user_id", String(userId));
        if (botName) params.append("bot_name", botName);
        if (majorJobId != null) params.append("major_job_id", String(majorJobId));
        const queryString = params.toString();
        return `/tasks/jobs/${queryString ? `?${queryString}` : ""}`;
      },
    }),

    getTaskJob: builder.query<Record<string, unknown>, number>({
      query: (jobId) => `/tasks/jobs/${jobId}/`,
    }),

    updateTaskJob: builder.mutation<
      Record<string, unknown>,
      { jobId: number; body: Record<string, unknown> }
    >({
      query: ({ jobId, body }) => ({
        url: `/tasks/jobs/${jobId}/`,
        method: "PATCH",
        body,
      }),
    }),

    refreshTaskJobStats: builder.mutation<Record<string, unknown>, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/refresh-stats/`,
        method: "POST",
      }),
    }),

    recalculateTaskJobRanges: builder.mutation<
      { job: Record<string, unknown>; tasks: Record<string, unknown>[] },
      { jobId: number; employee_number_from?: number; employee_number_to?: number }
    >({
      query: ({ jobId, ...body }) => ({
        url: `/tasks/jobs/${jobId}/recalculate-ranges/`,
        method: "POST",
        body,
      }),
    }),

    deleteTaskJob: builder.mutation<
      { message: string; deleted_task_ids: number[]; deleted_artifacts_count: number },
      { jobId: number; deleteLogs?: boolean }
    >({
      query: ({ jobId, deleteLogs }) => ({
        url: `/tasks/jobs/${jobId}/${deleteLogs ? "?delete_logs=true" : ""}`,
        method: "DELETE",
      }),
    }),

    runTasksBatch: builder.mutation<{ message: string }, Record<string, unknown>>({
      query: (body) => ({
        url: "/tasks/batch/run/",
        method: "POST",
        body,
      }),
    }),

    stopTasksBatch: builder.mutation<{ message: string }, Record<string, unknown>>({
      query: (body) => ({
        url: "/tasks/batch/stop/",
        method: "POST",
        body,
      }),
    }),

    updateConnection: builder.mutation<
      AdminVendorConnection,
      { connectionId: number; body: Partial<AdminVendorConnection> }
    >({
      query: ({ connectionId, body }) => ({
        url: `/api/admin/connections/${connectionId}/`,
        method: "PATCH",
        body,
      }),
    }),

    deleteConnection: builder.mutation<void, number>({
      query: (connectionId) => ({
        url: `/api/admin/connections/${connectionId}/`,
        method: "DELETE",
      }),
    }),

    runTask: builder.mutation<{ message: string }, number>({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/run/`,
        method: "POST",
      }),
    }),

    stopTask: builder.mutation<{ message: string }, number>({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/stop/`,
        method: "POST",
      }),
    }),

    setTaskPending: builder.mutation<
      { id: number; task_status: string; bot_work_status: string },
      number
    >({
      query: (taskId) => ({
        url: `/api/admin/tasks/${taskId}/set-pending/`,
        method: "PATCH",
      }),
    }),

    getAdminTask: builder.query<Record<string, unknown>, number>({
      query: (taskId) => `/api/admin/tasks/${taskId}/`,
    }),

    updateAdminTask: builder.mutation<
      Record<string, unknown>,
      { taskId: number; body: Record<string, unknown> }
    >({
      query: ({ taskId, body }) => ({
        url: `/tasks/${taskId}/`,
        method: "PATCH",
        body,
      }),
    }),

    deleteTask: builder.mutation<void, number>({
      query: (taskId) => ({
        url: `/tasks/${taskId}/`,
        method: "DELETE",
      }),
    }),

    getTask2FAStatus: builder.query<
      {
        task_id: number;
        status: string;
        has_code: boolean;
        requested_at?: string;
        expires_at?: string;
        submitted_at?: string;
        consumed_at?: string;
      },
      number
    >({
      query: (taskId) => `/tasks/${taskId}/2fa/`,
    }),

    submitTask2FACode: builder.mutation<
      {
        task_id: number;
        status: string;
        has_code: boolean;
      },
      { taskId: number; code: string }
    >({
      query: ({ taskId, code }) => ({
        url: `/tasks/${taskId}/2fa/`,
        method: "PATCH",
        body: { code },
      }),
    }),

    createBackendUser: builder.mutation<
      BackendUser,
      {
        username: string;
        first_name: string;
        client_id: number;
      }
    >({
      query: ({ username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/`,
        method: "POST",
        body: {
          username,
          first_name,
          client_id,
          user_type: "backend",
        },
      }),
      transformResponse: (
        u: PlatformUser & {
          sftp_connection_count?: number;
          platform_integration_count?: number;
          major_job_count?: number;
        },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        platform_integration_count: u.platform_integration_count ?? 0,
        sftp_count: u.sftp_connection_count ?? 0,
        major_job_count: u.major_job_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    updateBackendUser: builder.mutation<
      BackendUser,
      {
        id: number;
        username?: string;
        first_name?: string;
        client_id?: number;
      }
    >({
      query: ({ id, username, first_name, client_id }) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "PATCH",
        body: {
          ...(username !== undefined ? { username } : {}),
          ...(first_name !== undefined ? { first_name } : {}),
          ...(client_id !== undefined ? { client_id } : {}),
        },
      }),
      transformResponse: (
        u: PlatformUser & {
          sftp_connection_count?: number;
          platform_integration_count?: number;
          major_job_count?: number;
        },
      ): BackendUser => ({
        id: u.id,
        username: u.username,
        first_name: u.first_name ?? "",
        last_name: u.last_name ?? "",
        client_name: u.client_name ?? null,
        client_id: u.client_id ?? null,
        folder_count: u.folder_count,
        integration_count: u.integration_count,
        platform_integration_count: u.platform_integration_count ?? 0,
        sftp_count: u.sftp_connection_count ?? 0,
        major_job_count: u.major_job_count ?? 0,
        date_joined: u.date_joined,
        last_login: u.last_login,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    deleteBackendUser: builder.mutation<
      { message: string; s3_cleanup_queued?: boolean; job_id?: string },
      number
    >({
      query: (id) => ({
        url: `${BACKEND_BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    /** Parent automation grouping (connections pool + child task jobs). */
    getMajorJobs: builder.query<MajorJob[], number>({
      query: (userId) => `/tasks/major-jobs/?user_id=${userId}`,
      transformResponse: (response: MajorJob[] | { results?: MajorJob[] } | null | undefined) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        return Array.isArray(response.results) ? response.results : [];
      },
      providesTags: (_result, _error, userId) => [{ type: "MajorJobs", id: userId }],
    }),

    createMajorJob: builder.mutation<
      MajorJob,
      {
        userId: number;
        name: string;
        vendor: string;
        bot_kind: string;
        included_connection_ids?: number[];
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `/tasks/major-jobs/?user_id=${userId}`,
        method: "POST",
        body: {
          ...body,
          included_connection_ids: body.included_connection_ids ?? [],
        },
      }),
      invalidatesTags: (_r, _e, { userId }) => [{ type: "MajorJobs", id: userId }],
    }),

    updateMajorJob: builder.mutation<
      MajorJob,
      {
        majorJobId: number;
        body: Partial<{
          name: string;
          vendor: string;
          bot_kind: string;
          included_connection_ids: number[];
        }>;
        invalidateUserId: number;
      }
    >({
      query: ({ majorJobId, body }) => ({
        url: `/tasks/major-jobs/${majorJobId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { invalidateUserId }) => [{ type: "MajorJobs", id: invalidateUserId }],
    }),

    deleteMajorJob: builder.mutation<
      void,
      { majorJobId: number; invalidateUserId: number; deleteLogs?: boolean }
    >({
      query: ({ majorJobId, deleteLogs }) => ({
        url: `/tasks/major-jobs/${majorJobId}/${deleteLogs ? "?delete_logs=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { invalidateUserId }) => [{ type: "MajorJobs", id: invalidateUserId }],
    }),

    tasksBatchDelete: builder.mutation<
      { deleted_task_ids: number[]; missing_task_ids: number[] },
      { task_ids: number[] }
    >({
      query: ({ task_ids }) => ({
        url: "/tasks/batch/delete-tasks/",
        method: "POST",
        body: { task_ids },
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    taskJobsBatchDelete: builder.mutation<
      {
        deleted_job_ids: number[];
        deleted_task_ids: number[];
        deleting_artifacts_queued_for_jobs: number[];
        errors: { job_id: number; error: string }[];
      },
      { job_ids: number[]; delete_logs?: boolean }
    >({
      query: (body) => ({
        url: "/tasks/batch/delete-jobs/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    getJobStatusLogs: builder.query<
      { logs: string; job_id: number },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/status-logs/`,
    }),

    getJobFileLogs: builder.query<
      { logs: string; job_id: number },
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/file-logs/`,
    }),

    // One .zip bundling every per-task log zip of the job (manual inspection).
    getJobLogsArchive: builder.query<Blob, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/logs-archive/`,
        // API Gateway only base64-decodes binary bodies when the request's Accept
        // matches a BinaryMediaType; without this the zip arrives as a corrupt string.
        headers: { Accept: "application/zip" },
        responseHandler: (response: Response) => response.blob(),
        cache: "no-cache",
      }),
    }),

    // Wipe all stored log zips of the job → next run re-processes everyone.
    clearJobLogs: builder.mutation<{ job_id: number; deleted: number }, number>({
      query: (jobId) => ({
        url: `/tasks/jobs/${jobId}/logs/`,
        method: "DELETE",
      }),
    }),

    getTaskStatusLogs: builder.query<{ logs: string; task_id: number }, number>({
      query: (taskId) => `/tasks/${taskId}/status-logs/`,
    }),

    getTaskFileLogs: builder.query<{ logs: string; task_id: number }, number>({
      query: (taskId) => `/tasks/${taskId}/file-logs/`,
    }),

    // Full task log history (the bot's all_logs file). Survives ECS task
    // restarts, unlike CloudWatch live logs. Frontend turns it into a download.
    getTaskAllLogs: builder.query<
      { logs: string; found: boolean; task_id: number },
      number
    >({
      query: (taskId) => `/tasks/${taskId}/all-logs/`,
    }),

    // Per-task success/error counts computed from the status-log file (falls
    // back to live DB counters when the log zip isn't uploaded yet).
    getTaskEmployeeStats: builder.query<
      {
        task_id: number;
        success: number;
        error: number;
        found: boolean;
        source: "status_logs" | "db";
      },
      number
    >({
      query: (taskId) => `/tasks/${taskId}/employee-stats/`,
    }),

    // CloudWatch history: no params → returns {local, range}; with start/end
    // (ms epoch) + optional next_token → returns {local, events, next_token}.
    getTaskCloudWatchLogs: builder.query<
      {
        local: boolean;
        range?: { first: number | null; last: number | null } | null;
        events?: Array<{ timestamp: number | null; message: string }>;
        next_token?: string | null;
      },
      { taskId: number; start?: number; end?: number; nextToken?: string }
    >({
      query: ({ taskId, start, end, nextToken }) => {
        const params = new URLSearchParams();
        if (start != null) params.append("start", String(start));
        if (end != null) params.append("end", String(end));
        if (nextToken) params.append("next_token", nextToken);
        const qs = params.toString();
        return `/tasks/${taskId}/cloudwatch-logs/${qs ? `?${qs}` : ""}`;
      },
    }),

    getJobMatchesFiles: builder.query<
      Array<{ filename: string; exists: boolean; size_bytes: number | null; source: string | null }>,
      number
    >({
      query: (jobId) => `/tasks/jobs/${jobId}/matches-files/`,
    }),

    getJobMatchesFileContent: builder.query<
      { filename: string; content: string },
      { jobId: number; filename: string }
    >({
      query: ({ jobId, filename }) => `/tasks/jobs/${jobId}/matches-files/${filename}/`,
    }),

    deleteJobMatchesFile: builder.mutation<void, { jobId: number; filename: string }>({
      query: ({ jobId, filename }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/${filename}/`,
        method: "DELETE",
      }),
    }),

    presignJobMatchesMergeUpload: builder.mutation<
      {
        upload_url: string;
        key: string;
        staging_id: string;
        file_name: string;
        headers: Record<string, string>;
      },
      { jobId: number; file_name: string; content_type?: string }
    >({
      query: ({ jobId, file_name, content_type }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/merge/presign/`,
        method: "POST",
        body: {
          file_name,
          content_type: content_type || "application/octet-stream",
        },
      }),
    }),

    mergeJobMatchesReports: builder.mutation<
      { rows_added: number; matches_filename: string },
      { jobId: number; staging_key: string }
    >({
      query: ({ jobId, staging_key }) => ({
        url: `/tasks/jobs/${jobId}/matches-files/merge/`,
        method: "POST",
        body: { staging_key },
      }),
    }),

    getMajorJobSsnJob: builder.query<
      {
        job: Record<string, unknown> | null;
        task: Record<string, unknown> | null;
        ssn_file_exists: boolean | null;
      },
      number
    >({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/ssn-job/`,
    }),

    createMajorJobSsnJob: builder.mutation<
      {
        job: Record<string, unknown> | null;
        task: Record<string, unknown> | null;
        ssn_file_exists: boolean | null;
      },
      { majorJobId: number; connection_id?: number }
    >({
      query: ({ majorJobId, connection_id }) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/`,
        method: "POST",
        body: connection_id != null ? { connection_id } : {},
      }),
    }),

    deleteMajorJobSsnJob: builder.mutation<void, number>({
      query: (majorJobId) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/`,
        method: "DELETE",
      }),
    }),

    deleteMajorJobSsnFile: builder.mutation<void, number>({
      query: (majorJobId) => ({
        url: `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/`,
        method: "DELETE",
      }),
    }),

    getMajorJobSsnFileContent: builder.query<
      { filename: string; content: string },
      number
    >({
      query: (majorJobId) => `/tasks/major-jobs/${majorJobId}/ssn-job/s3-file/`,
    }),
  }),
});

export { adminUsersApi };

export const {
  useGetAdminUsersQuery,
  useLazyGetAdminUsersQuery,
  useGetAdminUserQuery,
  useLazyCheckPlatformUsernameAvailabilityQuery,
  useLazyCheckPlatformEmailAvailabilityQuery,
  useUpdateAdminUserMutation,
  useCreateAdminUserMutation,
  useArchiveAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useGenerateAdminUserPasswordMutation,
  useGetAdminUserFoldersQuery,
  useGetAssignableFoldersQuery,
  useGetAssignableIntegrationsQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
  useUpdateFolderSubfoldersMutation,
  useLazyGetFolderSubfoldersQuery,
  useGetAdminUserIntegrationsQuery,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
  useUpdateAdminUserPermissionsMutation,
  useGetBackendUsersQuery,
  useLazyGetBackendUsersQuery,
  useLazyCheckBackendUsernameAvailabilityQuery,
  useLazyCheckBackendEmailAvailabilityQuery,
  useGetBackendUserFoldersQuery,
  useGetBackendUserStorageFoldersQuery,
  useGetBackendUserIntegrationsQuery,
  useGetBackendUserBotConnectionsQuery,
  useLazyGetBackendUserBotConnectionsQuery,
  useCreateBackendUserBotConnectionMutation,
  useGetConnectionTasksQuery,
  useLazyGetConnectionTasksQuery,
  useCreateConnectionTaskMutation,
  useAddTasksToJobMutation,
  useCreateTasksBatchMutation,
  useGetBotRegistryQuery,
  useUpdateConnectionMutation,
  useDeleteConnectionMutation,
  useGetTaskJobsQuery,
  useGetTaskJobQuery,
  useLazyGetTaskJobQuery,
  useUpdateTaskJobMutation,
  useRefreshTaskJobStatsMutation,
  useRecalculateTaskJobRangesMutation,
  useDeleteTaskJobMutation,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSetTaskPendingMutation,
  useGetAdminTaskQuery,
  useUpdateAdminTaskMutation,
  useDeleteTaskMutation,
  useGetTask2FAStatusQuery,
  useSubmitTask2FACodeMutation,
  useCreateBackendUserMutation,
  useUpdateBackendUserMutation,
  useDeleteBackendUserMutation,
  useGetMajorJobsQuery,
  useCreateMajorJobMutation,
  useUpdateMajorJobMutation,
  useDeleteMajorJobMutation,
  useTasksBatchDeleteMutation,
  useTaskJobsBatchDeleteMutation,
  useLazyGetJobStatusLogsQuery,
  useLazyGetJobFileLogsQuery,
  useLazyGetJobLogsArchiveQuery,
  useClearJobLogsMutation,
  useLazyGetTaskStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskAllLogsQuery,
  useLazyGetTaskEmployeeStatsQuery,
  useLazyGetTaskCloudWatchLogsQuery,
  useLazyGetJobMatchesFilesQuery,
  useLazyGetJobMatchesFileContentQuery,
  useDeleteJobMatchesFileMutation,
  usePresignJobMatchesMergeUploadMutation,
  useMergeJobMatchesReportsMutation,
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useLazyGetMajorJobSsnJobQuery,
  useCreateMajorJobSsnJobMutation,
  useDeleteMajorJobSsnJobMutation,
  useDeleteMajorJobSsnFileMutation,
  useLazyGetMajorJobSsnFileContentQuery,
  useArchiveClientMutation,
  useLazyCheckClientCodeAvailabilityQuery,
  useLazyCheckClientNameAvailabilityQuery,
} = adminUsersApi;
